/* ============================================================
   Weight calibration by operator type.

   The eight domain weights are struck for an integrated operator —
   somebody who moves their own goods on their own trucks and clears
   them themselves. Almost nobody is that.

   A shipper who owns no trucks is scored 14% on Fleet & transport, a
   domain about vehicles they do not have. The existing "not scored"
   option removes an individual question from its domain's base, which
   is the right tool for "we have no bonded warehouse" — one question,
   one operator. It is the wrong tool for a whole domain that does not
   apply to an entire class of user: they would have to mark four
   questions inapplicable and the domain would then vanish from the
   index entirely, taking its 14% with it and telling them nothing.

   Reweighting says something different and truer. Fleet still matters
   to a shipper — a carrier's fleet age shows up in their transit times
   — it just matters less than customs does. So the weight drops rather
   than disappearing.

   The four types are not invented here. They are the four audiences
   the practice already addresses by name: shippers and importers,
   transporters, clearing agents, and the integrated operator the base
   weights describe.

   ---- Why one axis and not two ----

   The sister platform calibrates on two axes, fleet type and operating
   model, because both are properties of the carrier that no single
   calculation reveals. The obvious second axis here would be mode —
   road, rail, multimodal — and it is deliberately absent: mode is
   already a per-run input the user sets in the profile and in each
   calculator. Folding it into the scorecard weights would let one
   variable move the answer twice, in two places, with no indication
   that it had.

   ---- Why the deltas are small ----

   The largest single adjustment below is 6 points. A calibration that
   can swing a domain from 16% to 40% is not a calibration, it is four
   different scorecards wearing one name — and it makes two operators'
   indices incomparable, which is the one thing an index is for. These
   move the emphasis; they do not rewrite the instrument.
   ============================================================ */

import { DOMAINS } from './domains.js';

/** The unadjusted weights, read from the domain registry rather than
    restated — a second copy of the base weights is a second thing to
    keep in step, and they would drift the first time one was edited. */
export const BASE_WEIGHTS = Object.fromEntries(DOMAINS.map((d) => [d.id, d.weight]));

export const OPERATOR_TYPES = [
  {
    id: 'integrated',
    label: 'Integrated operator',
    hint: 'Moves own goods, own fleet, clears in-house',
    /* The base case. Stated explicitly rather than left as the empty
       default, so choosing it is a decision the user made rather than
       one they failed to make. */
    deltas: {}
  },
  {
    id: 'shipper',
    label: 'Shipper or importer',
    hint: 'Owns the cargo, buys transport',
    deltas: { fleet: -6, customs: +3, docs: +2, cost: +1 }
  },
  {
    id: 'transporter',
    label: 'Transporter or haulier',
    hint: 'Owns the trucks, moves other people’s cargo',
    deltas: { fleet: +5, corridor: +2, customs: -4, docs: -3 }
  },
  {
    id: 'agent',
    label: 'Clearing and forwarding agent',
    hint: 'Clears and documents, rarely owns the vehicle',
    deltas: { customs: +5, docs: +4, safety: +2, fleet: -6, cost: -5 }
  }
];

export const DEFAULT_OPERATOR = 'integrated';

/**
 * Apply an operator type's deltas and apportion back to exactly 100.
 *
 * Largest-remainder rather than the simpler "give the last domain
 * whatever is left". That shortcut is invisible in a total — it always
 * sums to 100 — while quietly dumping the entire rounding error onto
 * one fixed domain, which is then wrong by a point or two in every
 * calibrated profile. Here the error goes to whichever domains were
 * actually closest to rounding up, so no single domain absorbs it.
 *
 * @param {string} operatorId
 * @returns {Record<string, number>} weights summing to exactly 100
 */
export function weightsFor(operatorId) {
  const type = OPERATOR_TYPES.find((t) => t.id === operatorId);
  if (!type || !Object.keys(type.deltas).length) return { ...BASE_WEIGHTS };

  /* A floor of 4 rather than 0. A domain worth nothing should be
     removed from the instrument, not weighted to nothing inside it —
     and a visible 4% invites the reader to disagree with the
     calibration, which a silent 0% does not. */
  const FLOOR = 4;
  const raw = {};
  for (const [id, base] of Object.entries(BASE_WEIGHTS)) {
    raw[id] = Math.max(FLOOR, base + (type.deltas[id] || 0));
  }

  const total = Object.values(raw).reduce((a, b) => a + b, 0);
  const exact = Object.entries(raw).map(([id, v]) => ({ id, value: (v / total) * 100 }));

  const out = {};
  let assigned = 0;
  for (const e of exact) {
    out[e.id] = Math.floor(e.value);
    assigned += out[e.id];
  }
  /* Hand the remaining points to the largest fractional parts. */
  const order = exact
    .map((e) => ({ id: e.id, frac: e.value - Math.floor(e.value) }))
    .sort((a, b) => b.frac - a.frac);
  let remaining = 100 - assigned;
  for (let i = 0; remaining > 0; i = (i + 1) % order.length) {
    out[order[i].id] += 1;
    remaining -= 1;
  }
  return out;
}

/** What changed, for the sentence the scorecard prints under the picker. */
export function describeCalibration(operatorId) {
  const type = OPERATOR_TYPES.find((t) => t.id === operatorId);
  if (!type || !Object.keys(type.deltas).length) return null;
  const adjusted = weightsFor(operatorId);
  const moved = DOMAINS.map((d) => ({ name: d.name, from: d.weight, to: adjusted[d.id] }))
    .filter((m) => m.from !== m.to)
    .sort((a, b) => Math.abs(b.to - b.from) - Math.abs(a.to - a.from));
  return { label: type.label, moved };
}
